import type { ComponentType } from "react";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { PropertyCard } from "./PropertyCard";
import { AddMortgage } from "./AddMortgage";
import { ExtractedMortgage } from "./ExtractedMortgages";

const propertyCardCache = new Map<number, ComponentType<FlowCustomComponentProps>>();
const addMortgageCache = new Map<string, ComponentType<FlowCustomComponentProps>>();
const extractedMortgageCache = new Map<string, ComponentType<FlowCustomComponentProps>>();

export function getPropertyCardComponent(
  propertyId: number,
): ComponentType<FlowCustomComponentProps> {
  const cached = propertyCardCache.get(propertyId);
  if (cached) return cached;

  const Component = (props: FlowCustomComponentProps) => (
    <PropertyCard {...props} propertyId={propertyId} />
  );

  propertyCardCache.set(propertyId, Component);
  return Component;
}

export function getExtractedMortgageComponent(
  mortgageId: number | null,
  isFirst: boolean,
): ComponentType<FlowCustomComponentProps> {
  const cacheKey = `${mortgageId}:${isFirst}`;
  const cached = extractedMortgageCache.get(cacheKey);
  if (cached) return cached;

  const Component = (props: FlowCustomComponentProps) => (
    <ExtractedMortgage {...props} mortgageId={mortgageId} isFirst={isFirst} />
  );

  extractedMortgageCache.set(cacheKey, Component);
  return Component;
}

export function getAddMortgageForScreen(
  screenId: string,
  nextScreenId?: string,
  clearPrefill?: boolean,
  linkedPropertyId?: number,
  loopBackTo?: string,
): ComponentType<FlowCustomComponentProps> {
  const cacheKey = `${screenId}:${nextScreenId ?? "end"}:${clearPrefill ? "clear" : "keep"}:${linkedPropertyId ?? ""}:${loopBackTo ?? ""}`;
  const cached = addMortgageCache.get(cacheKey);
  if (cached) return cached;

  const Component = (props: FlowCustomComponentProps) => {
    const handleComplete = () => {
      if (nextScreenId) {
        props.goToScreen(nextScreenId, {
          ...props.answers,
          [screenId]: "done",
        });
      } else if (loopBackTo) {
        props.goToScreen(loopBackTo, { ...props.answers, [loopBackTo]: "", [screenId]: "" });
      } else {
        props.onComplete();
      }
    };
    let answers = clearPrefill
      ? {
          ...props.answers,
          prefill_mortgage_id: "",
          prefill_lender_name: "",
          prefill_balance: "",
          prefill_property_address: "",
        }
      : props.answers;
    if (linkedPropertyId !== undefined) {
      answers = { ...answers, prefill_linked_property_id: String(linkedPropertyId) };
    }
    return <AddMortgage {...props} answers={answers} onComplete={handleComplete} />;
  };

  addMortgageCache.set(cacheKey, Component);
  return Component;
}
